import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { styled, keyframes } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CloseIcon from "@mui/icons-material/Close";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import {
  stopGuide,
  nextStep,
  setGuideNavigating,
  setGuideTransit,
  skipGuideStep,
  completeGuideStep,
} from "store/slice/globalControl/globalControlSlice";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";

/* ─────────────────────────────────────────────
   THEME COLOR MAP — mirrors the dashboard palette
   so the guide always matches the app's sidenavColor
───────────────────────────────────────────── */
const THEME_COLOR_MAP = {
  info:      { main: "#1A73E8", light: "#49a3f1" },
  primary:   { main: "#e91e63", light: "#EC407A" },
  success:   { main: "#4caf50", light: "#66bb6a" },
  warning:   { main: "#fb8c00", light: "#ffa726" },
  error:     { main: "#F44335", light: "#ef5350" },
  dark:      { main: "#344767", light: "#42424a" },
  secondary: { main: "#7b809a", light: "#8f93a9" },
  light:     { main: "#7b809a", light: "#adb5bd" },
};

/** Resolve a theme color key to a hex string */
const resolveThemeColor = (key) => {
  const entry = THEME_COLOR_MAP[key] || THEME_COLOR_MAP.info;
  return entry.main;
};

/** Lighter variant of the theme color for text/icons */
const resolveThemeColorLight = (key) => {
  const entry = THEME_COLOR_MAP[key] || THEME_COLOR_MAP.info;
  return entry.light;
};

/* ─────────────────────────────────────────────
   STEP CONFIGS — one entry per step key
───────────────────────────────────────────── */
export const GUIDE_STEPS = {
  PLACE_SETTING: {
    targetId: "place-setting-btn",
    title: "Configure Place Settings",
    description:
      "Now that your place is created, update your place settings to configure it for your business needs.",
    hint: "Click the highlighted settings icon to proceed.",
    action: "CLICK",
    step: 1,
    icon: "⚙️",
    module: "Settings",
    shortTitle: "Place Settings",
  },
  PRICING_TIER: {
    targetId: "pricing-tier-card",
    title: "Set Up Pricing Tier",
    description:
      "Configure your pricing tier — set payment gateway, fees, and revenue splits to get ready for transactions.",
    hint: "Interact with the highlighted card to edit and save your pricing settings.",
    action: "CUSTOM",
    step: 2,
    icon: "💰",
    module: "Pricing",
    shortTitle: "Pricing Tier",
  },
  GENERAL_SETTINGS: {
    targetId: "general-settings-tab",
    title: "General Settings",
    description:
      "Configure general platform settings like Plivo phone number and notification preferences.",
    hint: "Click the highlighted settings tab to proceed.",
    action: "CLICK",
    step: 3,
    icon: "🔧",
    module: "General",
    shortTitle: "General",
  },
  CREATE_RATE: {
    targetId: "create-rate-btn",
    title: "Create Your First Rate",
    description:
      "Define your parking rate structure. This is the pricing customers will see when booking.",
    hint: "Click the highlighted button to create your first rate.",
    action: "CLICK",
    step: 3,
    icon: "🏷️",
    module: "Rates",
    shortTitle: "Create Rate",
  },
  ASSIGN_RATE: {
    targetId: "assign-rate-btn",
    title: "Assign Rate to Lot",
    description:
      "Assign the rate you just created to your parking lot. This completes the setup!",
    hint: "Click the highlighted button to assign the rate.",
    action: "CLICK",
    step: 4,
    icon: "✅",
    module: "Assign",
    shortTitle: "Assign Rate",
  },
};

/* Role-based step sequences */
const SUPER_ADMIN_FLOW = {
  PLACE_SETTING: { next: "PRICING_TIER", route: "/pricing_tier" },
  PRICING_TIER: { next: "GENERAL_SETTINGS", route: "/settings?tab=plivoNumber" },
  GENERAL_SETTINGS: { next: null, route: null },
};

const BRAND_ADMIN_FLOW = {
  PLACE_SETTING: { next: "PRICING_TIER", route: "/pricing_tier" },
  PRICING_TIER: { next: "CREATE_RATE", route: "/rates" },
  CREATE_RATE: { next: "ASSIGN_RATE", route: "/assign_rates" },
  ASSIGN_RATE: { next: null, route: null },
};

// Legacy export for anything importing STEP_FLOW
export const STEP_FLOW = BRAND_ADMIN_FLOW;

const PAD = 10;
const CARD_W = 340;
const CARD_GAP = 20;

/* ─────────────────────────────────────────────
   KEYFRAMES
───────────────────────────────────────────── */
const shimmer = keyframes`
  0% { background-position: -200% center }
  100% { background-position: 200% center }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1) }
  50% { opacity: 0.4; transform: scale(0.85) }
`;

const ringExpand = keyframes`
  0% { transform: scale(1); opacity: 0.7 }
  100% { transform: scale(1.9); opacity: 0 }
`;

const cardSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.96) }
  to   { opacity: 1; transform: translateY(0) scale(1) }
`;

const backdropIn = keyframes`
  from { opacity: 0 }
  to   { opacity: 1 }
`;

const dotPop = keyframes`
  0%   { transform: scale(0); opacity: 0 }
  60%  { transform: scale(1.3) }
  100% { transform: scale(1); opacity: 1 }
`;

const successPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.4) }
  50%       { box-shadow: 0 0 0 10px rgba(16,185,129,0) }
`;

/* ─────────────────────────────────────────────
   CANVAS BACKDROP
───────────────────────────────────────────── */
function roundRect(ctx, x, y, w, h, r) {
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function renderBackdrop(canvas, rect) {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  const W = window.innerWidth;
  const H = window.innerHeight;

  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "rgba(0,0,0,0.68)";
  ctx.fillRect(0, 0, W, H);

  const x = rect.left - PAD;
  const y = rect.top - PAD;
  const w = rect.width + PAD * 2;
  const h = rect.height + PAD * 2;
  const r = 10;

  ctx.globalCompositeOperation = "destination-out";
  for (let i = 16; i >= 1; i--) {
    const exp = i * 4;
    ctx.fillStyle = `rgba(0,0,0,${(0.04 * (17 - i)) / 16})`;
    ctx.beginPath();
    roundRect(ctx, x - exp, y - exp, w + exp * 2, h + exp * 2, r + exp * 0.6);
    ctx.fill();
  }
  ctx.fillStyle = "rgba(0,0,0,1)";
  ctx.beginPath();
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";
}

/* ─────────────────────────────────────────────
   STYLED COMPONENTS
───────────────────────────────────────────── */

/** Expanding ring around target */
const RingEl = styled(Box)(({ delay = 0 }) => ({
  position: "fixed",
  pointerEvents: "none",
  zIndex: 10012,
  borderRadius: 12,
  border: "1.5px solid rgba(255,255,255,0.5)",
  animation: `${ringExpand} 2s ease-out ${delay}s infinite`,
}));

/** Solid border highlight */
const BorderEl = styled(Box)({
  position: "fixed",
  pointerEvents: "none",
  zIndex: 10015,
  borderRadius: 10,
  transition: "all 0.3s ease",
});

/** Arrow tip pointing to element */
const ArrowTip = styled(Box)(({ tipside }) => ({
  position: "fixed",
  zIndex: 10019,
  width: 14,
  height: 14,
  background: "linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",
  pointerEvents: "none",
  transform: "rotate(45deg)",
  ...(tipside === "below"
    ? {
        borderTop: "1px solid rgba(99,102,241,0.35)",
        borderLeft: "1px solid rgba(99,102,241,0.35)",
      }
    : {
        borderBottom: "1px solid rgba(99,102,241,0.35)",
        borderRight: "1px solid rgba(99,102,241,0.35)",
      }),
}));

/** Main guide card */
const GuideCard = styled(Paper)({
  position: "fixed",
  zIndex: 10020,
  width: CARD_W,
  background: "linear-gradient(160deg, #1e1b4b 0%, #0f172a 100%)",
  border: "1px solid rgba(99,102,241,0.3)",
  borderRadius: 20,
  padding: "20px 20px 18px",
  boxShadow:
    "0 24px 64px rgba(0,0,0,0.95), 0 0 0 1px rgba(99,102,241,0.15), inset 0 1px 0 rgba(255,255,255,0.06)",
  animation: `${cardSlideIn} 0.32s cubic-bezier(0.34,1.56,0.64,1) both`,
  overflow: "visible",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 20,
    background:
      "linear-gradient(145deg, rgba(99,102,241,0.08) 0%, transparent 60%)",
    pointerEvents: "none",
  },
});

/** Step dot indicator */
const StepDot = styled(Box)(({ dotstate = "idle", dotcolor = "#6366f1", delay = 0 }) => ({
  height: 6,
  borderRadius: 3,
  animation: `${dotPop} 0.35s cubic-bezier(0.34,1.56,0.64,1) ${delay}s both`,
  transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
  ...(dotstate === "idle" && { width: 6, background: "rgba(255,255,255,0.12)" }),
  ...(dotstate === "done" && { width: 6, background: "rgba(110,231,183,0.6)" }),
  ...(dotstate === "active" && {
    width: 28,
    background: dotcolor,
    backgroundSize: "200% 100%",
    boxShadow: `0 0 10px ${dotcolor}55`,
    animation: `${shimmer} 2s linear infinite`,
  }),
}));

/** Primary action button */
const ActionBtn = styled(Button)(({ btncolor = "#6366f1" }) => ({
  flex: 2,
  height: 40,
  background: `linear-gradient(135deg, ${btncolor} 0%, ${btncolor}dd 100%)`,
  border: "none",
  borderRadius: 12,
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  letterSpacing: 0,
  gap: 6,
  position: "relative",
  overflow: "hidden",
  transition: "all 0.2s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 12,
    background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 55%)",
    pointerEvents: "none",
  },
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: `0 8px 24px ${btncolor}55`,
    filter: "brightness(1.1)",
  },
  "&:active": { transform: "none", filter: "brightness(0.95)" },
}));

/** Skip button */
const SkipBtn = styled(Button)({
  flex: 1,
  height: 40,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 12,
  color: "rgba(255,255,255,0.4)",
  fontSize: 12.5,
  fontWeight: 500,
  textTransform: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.65)",
    border: "1px solid rgba(255,255,255,0.18)",
  },
});

/** Module badge */
const ModuleBadge = styled(Chip)(({ chipcolor = "#6366f1" }) => ({
  height: 22,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  background: `${chipcolor}22`,
  border: `1px solid ${chipcolor}44`,
  color: `${chipcolor}ee`,
  "& .MuiChip-label": { padding: "0 8px" },
}));

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const GuideOverlay = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  // ✅ Resolve actual hex from the app's theme color
  const themeColor = resolveThemeColor(sidenavColor);
  const themeColorLight = resolveThemeColorLight(sidenavColor);

  const { active, currentStep, navigating, completedSteps, userRole, transit } = useSelector(
    (s) => s.ui.guide,
  );
  const meRole = useSelector((s) => s.users?.meInfo?.roleTitle);

  // Resolve role: prefer from guide state (set at creation time), fallback to current user role
  const resolvedRole = userRole || meRole;

  // Role-based step flow
  const stepFlow = useMemo(
    () => (resolvedRole === "Super Admin" ? SUPER_ADMIN_FLOW : BRAND_ADMIN_FLOW),
    [resolvedRole],
  );

  // Ordered step keys for this role
  const stepKeys = useMemo(() => Object.keys(stepFlow), [stepFlow]);
  const totalSteps = stepKeys.length;

  const [targetRect, setTargetRect] = useState(null);
  const [targetEl, setTargetEl] = useState(null);
  const [isExiting, setIsExiting] = useState(false);

  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const stepConfig = GUIDE_STEPS[currentStep];
  const stepNum = stepConfig?.step || 1;
  // ✅ Always use the dashboard's sidenavColor — all steps share the same brand color
  const stepColor = themeColor;

  /* ── find element ── */
  useEffect(() => {
    if (!active || !stepConfig) return;
    let mounted = true;
    let attempts = 0;
    setTargetEl(null);
    setTargetRect(null);

    const find = () => {
      if (!mounted) return;
      const el = document.getElementById(stepConfig.targetId);
      if (el) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!mounted) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setTargetEl(el);
          });
        });
        return;
      }
      if (++attempts < 40) setTimeout(find, 250);
    };

    const t = setTimeout(find, 400);
    return () => {
      mounted = false;
      clearTimeout(t);
    };
  }, [active, currentStep, stepConfig]);

  /* ── RAF loop for rect tracking ── */
  useEffect(() => {
    if (!targetEl || !active) return;
    let mounted = true;
    const canvas = canvasRef.current;

    const loop = () => {
      if (!mounted) return;
      if (!document.body.contains(targetEl)) {
        setTargetEl(null);
        setTargetRect(null);
        return;
      }
      const r = targetEl.getBoundingClientRect();
      setTargetRect(r);
      if (canvas instanceof HTMLCanvasElement) renderBackdrop(canvas, r);
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      mounted = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [targetEl, active]);

  /* ── clear canvas when inactive ── */
  useEffect(() => {
    if (!active) {
      const canvas = canvasRef.current;
      if (canvas instanceof HTMLCanvasElement) {
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [active]);

  /* ── skip / advance ── */
  const advance = useCallback(() => {
    if (navigating) return;
    const flow = stepFlow[currentStep];
    dispatch(skipGuideStep(currentStep));
    dispatch(setGuideTransit(false)); // ensure transit is cleared on skip
    if (!flow?.next) {
      dispatch(stopGuide());
      return;
    }
    dispatch(nextStep(flow.next));
    navigate(`${flow.route}?guide=${flow.next}`, { replace: true });
  }, [currentStep, navigating, dispatch, navigate, stepFlow]);

  /* ── listen for external step-complete events ── */
  useEffect(() => {
    const handler = (e) => {
      const completed = e.detail?.step;
      if (!completed) return;
      // ✅ Only act on events when guide is running — checklist flow fires events too
      //    but those should only update DB/Redux (handled by getPlaces()), not navigate
      if (!active) return;
      if (completedSteps.includes(completed)) return;

      dispatch(completeGuideStep(completed));
      // ✅ Clear transit mode now that the action is done
      dispatch(setGuideTransit(false));

      const flow = stepFlow[completed];
      if (!flow?.next) {
        dispatch(stopGuide());
        return;
      }

      let nextStepKey = flow.next;
      while (nextStepKey && completedSteps.includes(nextStepKey)) {
        nextStepKey = stepFlow[nextStepKey]?.next;
      }

      if (!nextStepKey) {
        dispatch(stopGuide());
        return;
      }

      dispatch(nextStep(nextStepKey));
      navigate(`${stepFlow[completed].route}?guide=${nextStepKey}`, { replace: true });
    };

    window.addEventListener("GUIDE_STEP_COMPLETE", handler);
    return () => window.removeEventListener("GUIDE_STEP_COMPLETE", handler);
  }, [active, completedSteps, dispatch, navigate, stepFlow]);

  /* ── primary action ── */
  const handleAction = useCallback(() => {
    if (navigating || !targetEl) return;
    dispatch(setGuideNavigating(true));

    if (stepConfig.action === "CLICK") {
      // ✅ Enter transit mode: guide stays active so GUIDE_STEP_COMPLETE can still fire,
      //    but overlay card + click-blocker are hidden so user can interact with the page
      dispatch(setGuideTransit(true));
      setTargetEl(null);
      setTargetRect(null);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          targetEl.click();
          setTimeout(() => dispatch(setGuideNavigating(false)), 500);
        }),
      );
      return;
    }

    if (stepConfig.action === "CUSTOM") {
      dispatch(setGuideTransit(true));
      setTargetEl(null);
      setTargetRect(null);
      window.dispatchEvent(
        new CustomEvent("GUIDE_CUSTOM_ACTION", {
          detail: { step: currentStep },
        }),
      );
      setTimeout(() => dispatch(setGuideNavigating(false)), 500);
    }
  }, [navigating, targetEl, currentStep, stepConfig, dispatch]);

  /* ── cleanup ── */
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // ✅ In transit mode the guide is logically active but the UI is completely hidden
  //    so the user can freely interact with the settings/pricing page.
  //    guide.active stays true → isGuideActive checks in updateSettings etc. pass.
  if (!active || !currentStep || !stepConfig) return null;
  if (transit) return null; // hide everything while user performs action

  /* ── layout calculations ── */
  let cardStyle = { visibility: "hidden" };
  let tipStyle = { visibility: "hidden" };
  let tipSide = "below";
  let borderStyle = {};
  let ringStyle = {};

  if (targetRect) {
    const vW = document.documentElement.clientWidth;
    const vH = document.documentElement.clientHeight;
    const sL = targetRect.left - PAD;
    const sT = targetRect.top - PAD;
    const sW = targetRect.width + PAD * 2;
    const sH = targetRect.height + PAD * 2;

    borderStyle = { top: sT, left: sL, width: sW, height: sH };
    ringStyle = { top: sT - 8, left: sL - 8, width: sW + 16, height: sH + 16 };

    const cardH = 310;
    const below = vH - (sT + sH) >= cardH + CARD_GAP + 6;
    const cardTop = below
      ? sT + sH + CARD_GAP
      : Math.max(6, sT - CARD_GAP - cardH);
    const cardLeft = Math.max(
      10,
      Math.min(sL + sW / 2 - CARD_W / 2, vW - CARD_W - 10),
    );

    cardStyle = { top: cardTop, left: cardLeft, visibility: "visible" };
    tipSide = below ? "below" : "above";

    const tipX = Math.max(
      cardLeft + 14,
      Math.min(sL + sW / 2 - 7, cardLeft + CARD_W - 24),
    );
    tipStyle = {
      top: below ? cardTop - 7 : cardTop + cardH - 5,
      left: tipX,
      visibility: "visible",
    };
  }

  /* ── dot states ── */
  const dots = stepKeys.map((key, i) => {
    const s = GUIDE_STEPS[key];
    if (!s) return "idle";
    if (s.step < stepNum) return "done";
    if (s.step === stepNum) return "active";
    return "idle";
  });

  /* ── border color string ── */
  const borderBox = {
    ...borderStyle,
    boxShadow: `0 0 0 2px ${stepColor}, 0 0 0 6px ${stepColor}22, 0 0 32px 8px ${stepColor}11`,
  };

  return (
    <>
      {/* ── Backdrop canvas ── */}
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10002,
          pointerEvents: "none",
          animation: `${backdropIn} 0.3s ease both`,
        }}
      />

      {/* ── Click-blocker (ALWAYS active while guide is running) ── */}
      <Box
        sx={{ position: "fixed", inset: 0, zIndex: 10001 }}
        style={{ pointerEvents: "auto" }}
      />

      {/* ── Target highlight border ── */}
      {targetRect && (
        <BorderEl
          style={borderBox}
          sx={{
            transition: "box-shadow 0.4s ease, top 0.3s ease, left 0.3s ease",
          }}
        />
      )}

      {/* ── Pulse rings ── */}
      {targetRect && (
        <>
          <RingEl
            style={{ ...ringStyle, borderColor: `${stepColor}88` }}
            delay={0}
          />
          <RingEl
            style={{ ...ringStyle, borderColor: `${stepColor}55` }}
            delay={1}
          />
        </>
      )}

      {/* ── Arrow tip ── */}
      <ArrowTip
        tipside={tipSide}
        style={{
          ...tipStyle,
          // Dynamic border to match theme
          ...(tipSide === "below"
            ? {
                borderTop: `1px solid ${stepColor}55`,
                borderLeft: `1px solid ${stepColor}55`,
              }
            : {
                borderBottom: `1px solid ${stepColor}55`,
                borderRight: `1px solid ${stepColor}55`,
              }),
        }}
      />

      {/* ══════════════ GUIDE CARD ══════════════ */}
      <GuideCard
        key={currentStep}
        elevation={0}
        style={cardStyle}
        onClick={(e) => e.stopPropagation()}
        sx={{
          // ✅ Dynamic border tinted by the dashboard theme color
          border: `1px solid ${stepColor}44`,
          boxShadow: `0 24px 64px rgba(0,0,0,0.95), 0 0 0 1px ${stepColor}22, inset 0 1px 0 rgba(255,255,255,0.06)`,
          // Subtle color wash on the top-left corner of the card
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "20px",
            background: `linear-gradient(145deg, ${stepColor}12 0%, transparent 60%)`,
            pointerEvents: "none",
          },
        }}
      >
        {/* Close button */}
        <IconButton
          size="small"
          onClick={() => dispatch(stopGuide())}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.2)",
            padding: "4px",
            borderRadius: "8px",
            zIndex: 1,
            "&:hover": {
              color: "rgba(255,255,255,0.6)",
              background: "rgba(255,255,255,0.07)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>

        {/* ── TOP: Step dots + module badge ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: 1.75, pr: 3 }}>
          {dots.map((state, i) => (
            <StepDot
              key={i}
              dotstate={state}
              dotcolor={stepColor}
              delay={i * 0.05}
            />
          ))}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.75 }}>
            <ModuleBadge
              label={stepConfig.module}
              size="small"
              chipcolor={stepColor}
            />
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                color: "rgba(255,255,255,0.22)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {stepNum}/{totalSteps}
            </Typography>
          </Box>
        </Box>

        {/* ── Step breadcrumb trail ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            mb: 2,
            pb: 1.75,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {stepKeys.map((key, idx) => {
            const s = GUIDE_STEPS[key];
            if (!s) return null;
            const isDone = s.step < stepNum;
            const isActive = s.step === stepNum;
            return (
              <Box
                key={key}
                sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    px: "6px",
                    py: "3px",
                    borderRadius: "6px",
                    background: isActive ? `${stepColor}18` : "transparent",
                    border: isActive ? `1px solid ${stepColor}33` : "1px solid transparent",
                    transition: "all 0.25s ease",
                  }}
                >
                  {isDone ? (
                    <CheckCircleIcon sx={{ fontSize: 11, color: "rgba(110,231,183,0.8)" }} />
                  ) : isActive ? (
                    <FiberManualRecordIcon
                      sx={{
                        fontSize: 8,
                        color: stepColor,
                        animation: `${pulse} 1.4s ease-in-out infinite`,
                      }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 11, color: "rgba(255,255,255,0.15)" }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: isActive ? 700 : 400,
                      color: isDone
                        ? "rgba(110,231,183,0.7)"
                        : isActive
                        ? stepColor
                        : "rgba(255,255,255,0.2)",
                      letterSpacing: "0.02em",
                      whiteSpace: "nowrap",
                      transition: "color 0.25s ease",
                    }}
                  >
                    {s.shortTitle}
                  </Typography>
                </Box>
                {idx < stepKeys.length - 1 && (
                  <NavigateNextIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.12)" }} />
                )}
              </Box>
            );
          })}
        </Box>

        {/* ── Icon + Title ── */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.25 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: "12px",
              background: `${stepColor}18`,
              border: `1px solid ${stepColor}33`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              lineHeight: 1,
              boxShadow: `0 4px 16px ${stepColor}22`,
            }}
          >
            {stepConfig.icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 14.5,
                fontWeight: 700,
                color: "#f1f5f9",
                letterSpacing: "-0.02em",
                lineHeight: 1.3,
                mb: 0.25,
              }}
            >
              {stepConfig.title}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 500,
                color: stepColor,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Step {stepNum} of {totalSteps}
            </Typography>
          </Box>
        </Box>

        {/* ── Description ── */}
        <Typography
          sx={{
            fontSize: 12.5,
            lineHeight: 1.65,
            color: "rgba(255,255,255,0.5)",
            mb: 1.75,
          }}
        >
          {stepConfig.description}
        </Typography>

        {/* ── Hint pill ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: `${stepColor}0d`,
            border: `1px solid ${stepColor}22`,
            borderLeft: `3px solid ${stepColor}88`,
            borderRadius: "0 8px 8px 0",
            px: 1.25,
            py: 1,
            mb: 2,
          }}
        >
          {stepConfig.action === "CLICK" ? (
            <TouchAppIcon sx={{ fontSize: 14, color: stepColor, flexShrink: 0 }} />
          ) : (
            <TipsAndUpdatesIcon sx={{ fontSize: 14, color: stepColor, flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 500,
              color: `${stepColor}cc`,
              lineHeight: 1.4,
            }}
          >
            {stepConfig.hint}
          </Typography>
        </Box>

        {/* ── Progress bar ── */}
        <LinearProgress
          variant="determinate"
          value={(stepNum / totalSteps) * 100}
          sx={{
            mb: 2,
            height: 4,
            borderRadius: 2,
            background: "rgba(255,255,255,0.06)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              background: `linear-gradient(90deg, ${stepColor}99, ${stepColor})`,
              transition: "transform 0.5s cubic-bezier(0.4,0,0.2,1)",
            },
          }}
        />

        {/* ── Action buttons ── */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <SkipBtn
            variant="outlined"
            startIcon={<SkipNextIcon sx={{ fontSize: "14px !important" }} />}
            onClick={advance}
            disableRipple={false}
          >
            Skip
          </SkipBtn>
          <ActionBtn
            variant="contained"
            btncolor={stepColor}
            endIcon={
              <ArrowForwardIcon
                sx={{ fontSize: "14px !important", transition: "transform 0.2s" }}
              />
            }
            onClick={handleAction}
            disableElevation
            disabled={navigating || !targetEl}
            sx={{
              "&:hover .MuiButton-endIcon svg": { transform: "translateX(3px)" },
              "&.Mui-disabled": {
                opacity: 0.5,
                color: "#fff",
              },
            }}
          >
            {!targetEl && active
              ? "Finding element…"
              : stepConfig.action === "CLICK"
              ? "Take me there"
              : "Got it, proceed"}
          </ActionBtn>
        </Box>
      </GuideCard>
    </>
  );
};

export default GuideOverlay;
